"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useAuthStore, getAccessTokenAsync } from "@/store/useAuthStore";
import {
  LuBriefcase,
  LuBrain,
  LuUsers,
  LuRocket,
  LuHeart,
  LuMessageCircle,
  LuMapPin,
  LuClock,
  LuCalendar,
  LuBuilding2,
  LuChevronDown,
  LuChevronUp,
  LuArrowRight,
  LuBell,
  LuLoader,
} from "react-icons/lu";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

interface CareerRole {
  id: string;
  slug: string;
  title: string;
  roleType: string;
  location: string;
  duration: string;
  commitment: string;
  department: string;
  deadline: string;
}

const WHY_JOIN = [
  {
    icon: LuBrain,
    title: "Learn as you work",
    body: "You'll work on real problems across product, content, research, and operations. Every task teaches you something you can use.",
  },
  {
    icon: LuBriefcase,
    title: "Build useful experience",
    body: "Work with teams building a real product from scratch. Gain cross-functional exposure.",
  },
  {
    icon: LuRocket,
    title: "Be part of an early team",
    body: "You'll join at a foundational stage. What you contribute now shapes the platform thousands of students will use.",
  },
  {
    icon: LuHeart,
    title: "Create impact with your work",
    body: "Lattu AI helps Bangladeshi students make better decisions about studying abroad. Your work directly enables that, this is purpose-driven, not performative.",
  },
  {
    icon: LuMessageCircle,
    title: "Work with the founders directly",
    body: "No long chains of management. You'll have direct access to the people building this, with honest feedback and real mentorship.",
  },
];

const PROCESS_STEPS = [
  {
    n: 1,
    title: "You submit your application",
    body: "Fill out the role-specific form. Tell us who you are, what you've done, and why this work interests you. We read every application.",
  },
  {
    n: 2,
    title: "We review and respond",
    body: "Our team reviews applications within 60 days. If we think there's a fit, we'll reach out to schedule an initial conversation.",
  },
  {
    n: 3,
    title: "A short conversation",
    body: "This is usually a 30-minute call, not a formal interview. We want to understand how you think, what you've worked on, and what you're looking for.",
  },
  {
    n: 4,
    title: "A short task (for some roles)",
    body: "Depending on the role, we may ask you to complete a short practical task. This helps us see how you approach real work, not hypotheticals.",
  },
  {
    n: 5,
    title: "We make a decision and tell you",
    body: "We'll let you know either way. If we move forward, we'll share the next steps clearly; start date, expectations, and what the first few weeks look like.",
  },
];

const FAQS = [
  {
    q: "Do I need prior experience to apply?",
    a: "It depends on the role. Some roles are open to students and fresh graduates with no formal work history. Others require demonstrated experience. Read the role description carefully. We're specific about what we need.",
  },
  {
    q: "Are these paid positions?",
    a: "Compensation varies by role. Some positions are paid, some are unpaid internships with strong learning value, and some offer performance-based stipends. Each role listing will specify clearly.",
  },
  {
    q: "Can I apply for multiple roles?",
    a: "Yes, but only if you genuinely meet the requirements for each. Sending the same application to every open role signals low interest. Be specific.",
  },
  {
    q: "Is this remote or in-person?",
    a: "Most roles are remote-first. Some may have optional or required in-person components in Dhaka. The role listing will specify.",
  },
  {
    q: "How long does the hiring process take?",
    a: "We aim to complete the process within 2–3 weeks of your application. If we're taking longer, we'll let you know.",
  },
  {
    q: "What if I don't see a role that fits me?",
    a: "Use the Introduce Yourself form. We keep a running list of people we'd like to work with when the right opportunity comes up.",
  },
  {
    q: "I'm a student. Can I still apply?",
    a: "Yes. Many of our roles are designed for students and recent graduates. We care more about how you think and what you've done than your degree status.",
  },
  {
    q: "Will I get feedback if I don't get the role?",
    a: "We try to give brief feedback to candidates who reach the conversation stage. For initial applications, we may not always be able to provide individual feedback, but we'll always let you know our decision.",
  },
];

const ROLE_TYPE_BADGE: Record<string, string> = {
  Internship:
    "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  "Part-time":
    "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "Full-time":
    "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  Contractual:
    "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
};

function FAQAccordion() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/10 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden">
      {FAQS.map((faq, i) => (
        <div key={i} className="bg-white dark:bg-white/5">
          <button
            className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span className="font-semibold text-black dark:text-white pr-4">
              {faq.q}
            </span>
            {open === i ? (
              <LuChevronUp className="shrink-0 w-5 h-5 text-indigo-500" />
            ) : (
              <LuChevronDown className="shrink-0 w-5 h-5 text-zinc-400" />
            )}
          </button>
          {open === i && (
            <div className="px-6 pb-5 text-zinc-600 dark:text-zinc-400 leading-relaxed text-justify text-sm">
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Hero "Get notified" control, logged-in users see just a button (their
// account email is captured automatically); logged-out visitors see an email
// input. Both paths hit the same endpoint, which tags the subscriber with
// userId when a valid token is sent so both can be told apart later.
function HeroNotify() {
  const { email: accountEmail } = useAuthStore();
  const [inputEmail, setInputEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const subscribe = async (targetEmail: string) => {
    if (!API || status === "loading") return;
    setStatus("loading");
    try {
      const token = await getAccessTokenAsync();
      const res = await fetch(`${API}/careers/notify-subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: targetEmail }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.message || "Something went wrong. Please try again.",
        );
      }
      setStatus("success");
    } catch (err) {
      setErrorMsg(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-bold px-8 py-4">
        <LuBell className="w-5 h-5" /> You&apos;re on the list!
      </div>
    );
  }

  if (accountEmail) {
    return (
      <button
        type="button"
        onClick={() => subscribe(accountEmail)}
        disabled={status === "loading"}
        className="inline-flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white px-8 py-4 rounded-2xl text-base font-bold hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        {status === "loading" ? (
          <LuLoader className="w-5 h-5 animate-spin" />
        ) : (
          <LuBell className="w-5 h-5" />
        )}
        Get notified
      </button>
    );
  }

  return (
    <div className="w-full space-y-2">
      <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
        Get notified when we open a new role
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (inputEmail.trim()) subscribe(inputEmail.trim());
        }}
        className="w-fit flex flex-col sm:flex-row gap-3 justify-center items-center mx-auto"
      >
        <input
          type="email"
          required
          value={inputEmail}
          onChange={(e) => {
            setInputEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder="you@email.com"
          className="flex-1 sm:w-60 px-5 py-4 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500/40 transition-colors"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white px-8 py-4 rounded-2xl text-base font-bold hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 shrink-0"
        >
          {status === "loading" ? (
            <LuLoader className="w-5 h-5 animate-spin" />
          ) : (
            <LuBell className="w-5 h-5" />
          )}
          Get notified
        </button>
      </form>
      {status === "error" && (
        <p className="text-sm text-red-500 text-center sm:text-left">
          {errorMsg}
        </p>
      )}
    </div>
  );
}

function OpenRoles() {
  const [roles, setRoles] = useState<CareerRole[]>([]);
  const [loading, setLoading] = useState(!!API);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!API) return;
    fetch(`${API}/careers`)
      .then((r) => r.json())
      .then((data) => {
        setRoles(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LuLoader className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error || roles.length === 0) {
    return (
      <div className="text-center py-16 space-y-4">
        <p className="text-zinc-600 dark:text-zinc-400 text-lg">
          No open roles right now.
        </p>
        <p className="text-zinc-500 dark:text-zinc-500 text-sm max-w-md mx-auto">
          We post roles as the team grows. If you&apos;d like to be considered
          for future opportunities, introduce yourself, we keep a running list.
        </p>
        <a
          href="#stay-on-radar"
          className="inline-flex items-center gap-2 text-indigo-500 font-semibold text-sm hover:underline"
        >
          Stay on our radar <LuArrowRight className="w-4 h-4" />
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {roles.map((role) => (
        <Link
          key={role.id}
          href={`/careers/${role.slug}`}
          className="group p-6 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="font-bold text-black dark:text-white text-lg group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {role.title}
            </h3>
            <span
              className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${ROLE_TYPE_BADGE[role.roleType] ?? "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300"}`}
            >
              {role.roleType}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-zinc-500 dark:text-zinc-400 mb-4">
            <span className="flex items-center gap-1">
              <LuMapPin className="w-3.5 h-3.5" /> {role.location}
            </span>
            <span className="flex items-center gap-1">
              <LuClock className="w-3.5 h-3.5" /> {role.commitment}
            </span>
            <span className="flex items-center gap-1">
              <LuBuilding2 className="w-3.5 h-3.5" /> {role.department}
            </span>
          </div>
          <div className="flex items-center justify-between">
            {role.deadline ? (
              <span className="flex items-center gap-1 text-xs text-zinc-400 dark:text-zinc-500">
                <LuCalendar className="w-3.5 h-3.5" />
                Deadline:{" "}
                {new Date(role.deadline).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            ) : (
              <span></span>
            )}
            <span className="text-indigo-500 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
              View role <LuArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function CareersPage() {
  return (
    <div className="w-full bg-transparent">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-linear-to-b from-white dark:from-black via-white/60 dark:via-black/60 to-white dark:to-black" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-150 h-100 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />
        </div>
        <div className="relative max-w-4xl mx-auto px-8 py-16 lg:py-28 text-center space-y-8 w-full">
          <span className="text-xs uppercase tracking-[0.3em] text-indigo-500 font-bold block">
            Join the team
          </span>
          <h1 className="text-5xl lg:text-7xl font-black tracking-tighter leading-tight text-black dark:text-white">
            Careers at Lattu AI
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed text-justify max-w-3xl mx-auto">
            Help us build a clearer way for Bangladeshi students to plan their
            journey abroad. Lattu AI is built by a Bangladeshi team that
            understands how confusing this process can feel. We&apos;re creating
            tools that help students compare countries, understand costs,
            explore scholarships, and make better decisions before they apply.
            If you care about useful work, honest information, and building
            something from the ground up, we&apos;d love to hear from you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#open-roles"
              className="inline-flex items-center gap-2 bg-indigo-500 text-white px-8 py-4 rounded-2xl text-base font-bold hover:bg-indigo-600 hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(97,95,255,0.3)]"
            >
              View open roles <LuArrowRight className="w-5 h-5" />
            </a>
            <HeroNotify />
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="bg-[#ECEFF1] dark:bg-[#1a1a1a] py-24 transition-colors">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-14 space-y-4">
            <h2 className="text-4xl font-black tracking-tight text-black dark:text-white">
              Why join Lattu AI
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 text-center">
            {WHY_JOIN.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-6 space-y-4"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mx-auto">
                  <Icon className="w-5 h-5 text-indigo-500" />
                </div>
                <h3 className="font-bold text-black dark:text-white">
                  {title}
                </h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxedt">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section
        id="open-roles"
        className="py-24 bg-transparent max-w-7xl mx-auto px-8"
      >
        <div className="mb-12 space-y-3">
          <h2 className="text-4xl font-black tracking-tight text-black dark:text-white">
            Open roles
          </h2>
          <p className="text-zinc-600 dark:text-zinc-400 text-lg">
            Roles we&apos;re actively hiring for right now.
          </p>
        </div>
        <OpenRoles />
      </section>

      {/* Stay on Radar */}
      <section
        id="stay-on-radar"
        className="bg-[#ECEFF1] dark:bg-[#1a1a1a] py-24 transition-colors"
      >
        <div className="max-w-3xl mx-auto px-8 text-center space-y-6">
          <LuUsers className="w-10 h-10 text-indigo-500 mx-auto" />
          <h2 className="text-4xl font-black tracking-tight text-black dark:text-white">
            Interested in future opportunities?
          </h2>
          <p className="text-zinc-600 dark:text-zinc-400 text-lg leading-relaxed text-justify">
            We don&apos;t always have a role open that matches what you do. But
            we keep a list of people we&apos;d genuinely like to work with when
            the time comes. If you believe in what we&apos;re building and want
            to be considered as we grow, introduce yourself. We read these, and
            we remember them.
          </p>
          <Link
            href="/careers/introduce-yourself"
            className="inline-flex items-center gap-2 bg-indigo-500 text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-600 hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(97,95,255,0.3)]"
          >
            Introduce Yourself <LuArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-24 bg-transparent">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-14 space-y-3">
            <h2 className="text-4xl font-black tracking-tight text-black dark:text-white">
              How the application process works
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg">
              We keep it simple and honest.
            </p>
          </div>
          <div className="space-y-4">
            {PROCESS_STEPS.map((step) => (
              <div
                key={step.n}
                className="flex gap-6 p-6 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl"
              >
                <div className="shrink-0 w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-black text-lg shadow-[0_0_20px_rgba(97,95,255,0.4)]">
                  {step.n}
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-black dark:text-white">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed text-justify">
                    {step.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#ECEFF1] dark:bg-[#1a1a1a] py-24 transition-colors">
        <div className="max-w-3xl mx-auto px-8">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-4xl font-black tracking-tight text-black dark:text-white">
              Frequently asked questions
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg">
              Answers to things people usually ask before applying.
            </p>
          </div>
          <FAQAccordion />
          <div className="mt-12 text-center space-y-4">
            <p className="text-zinc-600 dark:text-zinc-400">
              Still have questions? We&apos;re happy to help.
            </p>
            <Link
              href="/contact-us"
              className="inline-flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white px-6 py-3 rounded-2xl font-bold hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:scale-105 active:scale-95 transition-all"
            >
              Contact us <LuArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
